import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { listBlogArticles } from '../api/client';
import type { BlogIndexEntry } from '../api/types';
import '../components/ui/ui-button';
import { SUBSTACK_PROFILE_URL } from '../lib/substack';
import { cardStyles, motionStyles, pageFrameStyles } from '../styles/lit-shared';

@customElement('blog-list-page')
export class BlogListPage extends LitElement {
  @state() private articles: BlogIndexEntry[] = [];
  @state() private loading = true;
  @state() private loadFailed = false;

  connectedCallback() {
    super.connectedCallback();
    void this.load();
  }

  private async load() {
    this.loading = true;
    this.loadFailed = false;
    try {
      const { articles } = await listBlogArticles();
      this.articles = articles;
    } catch {
      this.loadFailed = true;
      this.articles = [];
    } finally {
      this.loading = false;
    }
  }

  private formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  private openPost(url: string) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  private renderSubstackFallback(message: string) {
    return html`
      <div class="fallback">
        <p>${message}</p>
        <ui-button variant="primary" href=${SUBSTACK_PROFILE_URL}>
          Ir a mi Substack →
        </ui-button>
      </div>
    `;
  }

  static styles = [
    pageFrameStyles,
    cardStyles,
    motionStyles,
    css`
      .section-title {
        font-size: clamp(1.5rem, 3vw, 1.875rem);
        font-weight: 700;
        letter-spacing: -0.02em;
        margin-bottom: var(--space-sm);
      }

      .section-lead {
        color: var(--color-text-muted);
        font-size: 1rem;
        margin-bottom: var(--space-xl);
        max-width: 56ch;
      }

      .grid {
        display: grid;
        gap: var(--space-lg);
      }

      @media (min-width: 640px) {
        .grid {
          grid-template-columns: repeat(2, 1fr);
        }
      }

      @media (min-width: 960px) {
        .grid {
          grid-template-columns: repeat(3, 1fr);
        }
      }

      .post-card {
        cursor: pointer;
        overflow: hidden;
        padding: 0;
        height: 100%;
        display: flex;
        flex-direction: column;
        transition:
          transform var(--duration-normal) var(--ease-out),
          box-shadow var(--duration-normal) var(--ease-out),
          border-color var(--duration-normal) var(--ease-out);
      }

      .post-card:hover {
        transform: translateY(-2px);
        box-shadow: var(--shadow-glow-primary);
      }

      .post-card img {
        width: 100%;
        aspect-ratio: 16 / 9;
        object-fit: cover;
      }

      .post-card-body {
        padding: var(--space-lg);
        flex: 1;
        display: flex;
        flex-direction: column;
      }

      .post-card h2 {
        margin: var(--space-xs) 0;
        font-size: 1.125rem;
      }

      .post-card time {
        font-size: 0.75rem;
        color: var(--color-text-subtle);
      }

      .post-card p {
        margin: 0;
        flex: 1;
        font-size: 0.875rem;
        color: var(--color-text-muted);
      }

      .external-hint {
        margin-top: var(--space-sm);
        font-size: 0.75rem;
        font-weight: 600;
        color: var(--color-primary-light);
      }

      .tags {
        display: flex;
        flex-wrap: wrap;
        gap: 0.375rem;
        margin-top: var(--space-sm);
      }

      .tag {
        font-size: 0.6875rem;
        font-weight: 600;
        padding: 0.2rem 0.5rem;
        border-radius: var(--radius-full);
        background: rgb(139 92 246 / 12%);
        color: var(--color-primary-light);
      }

      .fallback {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--space-lg);
        text-align: center;
        padding: var(--space-2xl) var(--space-md);
        max-width: 42ch;
        margin-inline: auto;
      }

      .fallback p {
        margin: 0;
        color: var(--color-text-muted);
        line-height: 1.6;
      }

      .center {
        display: flex;
        justify-content: center;
        padding: var(--space-2xl);
      }
    `,
  ];

  render() {
    return html`
      <div class="animate-fade-up">
        <h1 class="section-title">Ideas sueltas, destellos y experimentos</h1>

        <p class="section-lead">
          Aquí van mis chispazos y experimentos, sin filtro ni red de seguridad. Caos creativo,
          ocurrencias y hallazgos: pulsa y salta directo a Substack si te atreves.
        </p>

        ${this.loading
          ? html`<div class="center"><div class="spinner" role="status"></div></div>`
          : this.loadFailed
            ? this.renderSubstackFallback(
                'Ahora mismo no puedo cargar los posts aquí. Puedes leerlos directamente en Substack.',
              )
            : this.articles.length === 0
              ? this.renderSubstackFallback(
                  'Aún no hay posts listados aquí. Mientras tanto, pásate por mi Substack.',
                )
              : html`
                  <div class="grid">
                    ${this.articles.map(
                      (post) => html`
                        <article
                          class="card post-card"
                          role="link"
                          tabindex="0"
                          @click=${() => this.openPost(post.url)}
                          @keydown=${(e: KeyboardEvent) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              this.openPost(post.url);
                            }
                          }}
                        >
                          ${post.coverImageUrl
                            ? html`<img
                                src=${post.coverImageUrl}
                                alt=""
                                loading="lazy"
                                referrerpolicy="no-referrer"
                              />`
                            : ''}
                          <div class="post-card-body">
                            <time datetime=${post.publishedAt}
                              >${this.formatDate(post.publishedAt)}</time
                            >
                            <h2>${post.title}</h2>
                            <p>${post.excerpt}</p>
                            ${post.tags?.length
                              ? html`
                                  <div class="tags">
                                    ${post.tags.map((tag) => html`<span class="tag">${tag}</span>`)}
                                  </div>
                                `
                              : ''}
                            <span class="external-hint">Leer en Substack →</span>
                          </div>
                        </article>
                      `,
                    )}
                  </div>
                `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'blog-list-page': BlogListPage;
  }
}
